import { Button } from '@app/components/core/button/Button'
import { TextInput } from '@app/components/core/input/text/TextInput'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { InputField } from '@app/components/core/input/InputField'
import { Text } from '@app/components/core/typography/Text'
import { useTranslations } from '@i18n/useLanguage'
import type { TextSectionType } from '@app/components/blocks/contentSection/ContentSection'
import {
  faChevronDown,
  faChevronUp,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'
import { type Style } from 'inlines'
import { Card } from '../card/Card'
import { lazy, Suspense } from 'react'

const LexicalRichHtmlInput = lazy(() =>
  import('@app/components/core/input/lexicalRichInput/LexicalRichHtmlInput').then(
    (module) => ({
      default: module.LexicalRichHtmlInput,
    }),
  ),
)

export type TextEditBlock = Omit<TextSectionType, 'truncationThreshold'>

export type ContentBlockEditorProps = {
  block: TextEditBlock
  onChange: (next: TextEditBlock) => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  canMoveUp: boolean
  canMoveDown: boolean
  canRemove?: boolean
  style?: Style
  titlePlaceholder?: string
  bodyPlaceholder?: string
}

export const ContentBlockEditor = ({
  block,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  canRemove = true,
  style,
  titlePlaceholder,
  bodyPlaceholder,
}: ContentBlockEditorProps) => {
  const translations = useTranslations()
  const t = translations.app.dashboard.pageContentEditor
  const common = translations.app.common

  return (
    <Card style={style}>
      <Column gap="1rem" alignItems="stretch">
        <TextInput
          label={t.titleLabel}
          text={block.title}
          setText={(title) => onChange({ ...block, title })}
          placeholder={titlePlaceholder ?? t.titlePlaceholder}
        />
        <Suspense
          fallback={
            <InputField label={t.bodyLabel}>
              <div
                style={{
                  minHeight: '8rem',
                  borderRadius: '0.75rem',
                  border: '1px solid var(--palette-blue-300)',
                  background: 'var(--palette-white)',
                  padding: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Text.Medium color="var(--palette-blue-400)">
                  {common.loading}
                </Text.Medium>
              </div>
            </InputField>
          }
        >
          <LexicalRichHtmlInput
            label={t.bodyLabel}
            value={block.body}
            setValue={(body) => {
              if (body === block.body) {
                return
              }
              onChange({ ...block, body })
            }}
            placeholder={bodyPlaceholder ?? t.bodyPlaceholder}
          />
        </Suspense>
        <Row
          gap="0.5rem"
          style={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}
        >
          <Button
            type="button"
            variant="secondary"
            size="small"
            icon={faChevronUp}
            label={t.moveUp}
            disabled={!canMoveUp}
            onClick={onMoveUp}
          />
          <Button
            type="button"
            variant="secondary"
            size="small"
            icon={faChevronDown}
            label={t.moveDown}
            disabled={!canMoveDown}
            onClick={onMoveDown}
          />
          <Button
            type="button"
            variant="secondary"
            size="small"
            icon={faTrash}
            label={t.removeBlock}
            disabled={!canRemove}
            onClick={onRemove}
          />
        </Row>
      </Column>
    </Card>
  )
}
